/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ChatContextHint,
  ChatMessageDto,
  ChatClarificationDto,
  ChatSuggestedActionDto,
  ChatSessionResponse,
  ChatCapabilityLinkDto,
  createChatSession,
  sendChatMessage,
} from '../api';
import { useAuth } from '../hooks/useAuth';

// Anonymous users get 1 free chat question to experience the value
const ANONYMOUS_CHAT_LIMIT = 1;
const ANONYMOUS_CHAT_STORAGE_KEY = 'guituitive_anonymous_chat_count';

type ChatRole = 'system' | 'user' | 'assistant';

export interface ChatViewMessage {
  id: string;
  role: ChatRole;
  content: string;
  timestamp: Date;
  citations?: ChatSessionResponse['messages'][number]['citations'];
  suggestions?: ChatSuggestedActionDto[];
  clarification?: ChatClarificationDto;
}

interface ChatContextValue {
  sessionId?: string;
  messages: ChatViewMessage[];
  isInitializing: boolean;
  isSending: boolean;
  pendingClarification?: ChatClarificationDto;
  suggestions: ChatSuggestedActionDto[];
  capabilityLinks: ChatCapabilityLinkDto[];
  hasReachedLimit: boolean;
  limitReachedFromServer: boolean;
  conversationCount: number;
  conversationLimit: number | 'unlimited';
  isAnonymous: boolean;
  anonymousChatUsed: boolean;
  teachingMode: boolean;
  teachingModeAvailable: boolean;
  setTeachingMode: (enabled: boolean) => void;
  ensureSession: () => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  resolveClarification: (optionId: string) => Promise<void>;
  registerContext: (hint: ChatContextHint) => string;
  unregisterContext: (key: string) => void;
  resetChat: (options?: { clearContexts?: boolean }) => Promise<void>;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

const mapRole = (role: ChatMessageDto['role']): ChatRole => {
  if (role === 0 || role === 'system') return 'system';
  if (role === 1 || role === 'user') return 'user';
  return 'assistant';
};

const normalizeMessage = (message: ChatMessageDto): ChatViewMessage => ({
  id: message.id,
  role: mapRole(message.role),
  content: message.content,
  timestamp: new Date(message.timestamp),
  citations: message.citations,
  suggestions: message.suggestions,
  clarification: message.clarification,
});

const computeContextKey = (hint: ChatContextHint) =>
  `${hint.scope}:${hint.entityId ?? ''}`;

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { hasReachedChatLimit, userProfile, getAuthToken, isAnonymous } = useAuth();
  const [session, setSession] = useState<ChatSessionResponse | null>(null);
  const [messages, setMessages] = useState<ChatViewMessage[]>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [limitReachedFromServer, setLimitReachedFromServer] = useState(false);
  const [pendingClarification, setPendingClarification] =
    useState<ChatClarificationDto | undefined>(undefined);
  const [suggestions, setSuggestions] = useState<ChatSuggestedActionDto[]>([]);
  const [capabilityLinks, setCapabilityLinks] = useState<ChatCapabilityLinkDto[]>([]);
  const [teachingMode, setTeachingMode] = useState(false);
  const [teachingModeAvailable, setTeachingModeAvailable] = useState(false);
  const sessionRef = useRef<ChatSessionResponse | null>(null);
  const contextsRef = useRef<Map<string, ChatContextHint>>(new Map());

  // Track anonymous user chat usage in localStorage
  const [anonymousChatCount, setAnonymousChatCount] = useState(0);

  // Load anonymous chat count from localStorage on mount
  useEffect(() => {
    if (isAnonymous) {
      const storedCount = localStorage.getItem(ANONYMOUS_CHAT_STORAGE_KEY);
      if (storedCount) {
        setAnonymousChatCount(parseInt(storedCount, 10) || 0);
      }
    }
  }, [isAnonymous]);

  // Determine if anonymous user has used their free chat
  const anonymousChatUsed = anonymousChatCount >= ANONYMOUS_CHAT_LIMIT;

  // Calculate limits based on user type
  const hasReachedLimit = isAnonymous
    ? anonymousChatUsed
    : (hasReachedChatLimit() || limitReachedFromServer);

  const conversationCount = isAnonymous
    ? anonymousChatCount
    : (userProfile?.features.currentHarmonicCompanionMessages || 0);

  const conversationLimit = isAnonymous
    ? ANONYMOUS_CHAT_LIMIT
    : (userProfile?.features.harmonicCompanionConversationsPerMonth || 0);

  const ensureSession = useCallback(async () => {
    if (sessionRef.current || isInitializing) {
      return;
    }
    if (hasReachedLimit) {
      console.warn('Chat conversation limit reached');
      return;
    }
    setIsInitializing(true);
    try {
      const token = await getAuthToken();
      const response = await createChatSession({
        clientVersion: 'web-0.1',
      }, token || undefined);
      sessionRef.current = response;
      setSession(response);
      setTeachingModeAvailable(response.teachingModeAvailable ?? false);
      if (response.messages?.length) {
        setMessages(response.messages.map(normalizeMessage));
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        console.warn('Chat conversation limit reached (403 from server)');
        setLimitReachedFromServer(true);
        // Don't throw - just log it. The UI will show the limit based on hasReachedLimit
      } else {
        console.error('Failed to create chat session:', error);
        throw error;
      }
    } finally {
      setIsInitializing(false);
    }
  }, [session, isInitializing, hasReachedLimit, getAuthToken]);

  const registerContext = useCallback((hint: ChatContextHint) => {
    const key = computeContextKey(hint);
    contextsRef.current.set(key, hint);
    return key;
  }, []);

  const unregisterContext = useCallback((key: string) => {
    contextsRef.current.delete(key);
  }, []);

  const appendMessages = useCallback((items: ChatMessageDto[] | undefined) => {
    if (!items || !items.length) {
      return;
    }
    setMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const incoming = items
        .map(normalizeMessage)
        .filter((msg) => !existingIds.has(msg.id));
      if (incoming.length === 0) {
        return prev;
      }
      return [...prev, ...incoming];
    });
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      if (!sessionRef.current && !isInitializing) {
        await ensureSession();
      }

      let activeSession = sessionRef.current;
      if (!activeSession) {
        // ensureSession failed
        return;
      }

      setIsSending(true);
      setPendingClarification(undefined);
      try {
        const contextHints = Array.from(contextsRef.current.values());
        const token = await getAuthToken();
        const response = await sendChatMessage(activeSession.sessionId, {
          content: trimmed,
          contextHints,
          stream: false,
          mode: teachingMode ? 'teaching' : undefined,
        }, token || undefined);

        appendMessages(response.additionalMessages);
        appendMessages([response.message]);
        setPendingClarification(response.clarification ?? undefined);
        setSuggestions(response.suggestions ?? []);
        setCapabilityLinks(response.capabilityLinks ?? []);

        // Increment anonymous chat count after successful message
        if (isAnonymous) {
          const newCount = anonymousChatCount + 1;
          setAnonymousChatCount(newCount);
          localStorage.setItem(ANONYMOUS_CHAT_STORAGE_KEY, newCount.toString());
        }
      } catch (error: any) {
        // Session lost (e.g. server restart during local dev) — recreate and retry once
        if (error.response?.status === 404) {
          console.warn('Chat session expired, recreating...');
          sessionRef.current = null;
          setSession(null);
          setMessages([]);
          try {
            const token = await getAuthToken();
            const freshSession = await createChatSession({
              clientVersion: 'web-0.1',
            }, token || undefined);
            sessionRef.current = freshSession;
            setSession(freshSession);

            const contextHints = Array.from(contextsRef.current.values());
            const retryResponse = await sendChatMessage(freshSession.sessionId, {
              content: trimmed,
              contextHints,
              stream: false,
              mode: teachingMode ? 'teaching' : undefined,
            }, token || undefined);

            appendMessages(retryResponse.additionalMessages);
            appendMessages([retryResponse.message]);
            setPendingClarification(retryResponse.clarification ?? undefined);
            setSuggestions(retryResponse.suggestions ?? []);
            setCapabilityLinks(retryResponse.capabilityLinks ?? []);

            if (isAnonymous) {
              const newCount = anonymousChatCount + 1;
              setAnonymousChatCount(newCount);
              localStorage.setItem(ANONYMOUS_CHAT_STORAGE_KEY, newCount.toString());
            }
          } catch (retryError) {
            console.error('Failed to recreate chat session', retryError);
          }
        } else {
          console.error('Failed to send chat message', error);
          const errorDetail = error.response?.data?.error || error.message || 'Something went wrong';
          appendMessages([{
            id: `error-${Date.now()}`,
            role: 'assistant' as ChatRole,
            content: `Sorry, an error occurred: ${errorDetail}`,
            timestamp: new Date().toISOString(),
          } as any]);
        }
      } finally {
        setIsSending(false);
      }
    },
    [session, ensureSession, appendMessages, isInitializing, getAuthToken, isAnonymous, anonymousChatCount, teachingMode]
  );

  const resolveClarification = useCallback(
    async (optionId: string) => {
      if (!pendingClarification) return;
      const option = pendingClarification.options.find(
        (o) => o.id === optionId
      );
      setPendingClarification(undefined);
      if (!option) return;

      const context = contextsRef.current.get(optionId);
      const description = option.label || context?.displayName || 'that';
      // Temporarily restrict contexts to the chosen one
      const existing = contextsRef.current.get(optionId);
      const hints = existing ? [existing] : Array.from(contextsRef.current.values());

      setIsSending(true);
      try {
        if (!sessionRef.current) {
          await ensureSession();
        }
        const activeSession = sessionRef.current;
        if (!activeSession) return;
        const token = await getAuthToken();
        const response = await sendChatMessage(activeSession.sessionId, {
          content: `I was referring to ${description}.`,
          contextHints: hints,
          stream: false,
          mode: teachingMode ? 'teaching' : undefined,
        }, token || undefined);
        appendMessages(response.additionalMessages);
        appendMessages([response.message]);
        setPendingClarification(response.clarification ?? undefined);
        setSuggestions(response.suggestions ?? []);
        setCapabilityLinks(response.capabilityLinks ?? []);
      } catch (error) {
        console.error('Failed to resolve clarification', error);
      } finally {
        setIsSending(false);
      }
    },
    [pendingClarification, session, ensureSession, appendMessages, getAuthToken, teachingMode]
  );

  const resetChat = useCallback(
    async (options?: { clearContexts?: boolean }) => {
      const shouldClearContexts = options?.clearContexts ?? true;
      sessionRef.current = null;
      setSession(null);
      setMessages([]);
      setSuggestions([]);
      setPendingClarification(undefined);
      setCapabilityLinks([]);
      if (shouldClearContexts) {
        contextsRef.current.clear();
      }
      try {
        await ensureSession();
      } catch (error) {
        // Error already handled in ensureSession
        console.error('Failed to reset chat:', error);
      }
    },
    [ensureSession]
  );

  const value = useMemo<ChatContextValue>(
    () => ({
      sessionId: sessionRef.current?.sessionId,
      messages,
      isInitializing,
      isSending,
      pendingClarification,
      suggestions,
      capabilityLinks,
      hasReachedLimit,
      limitReachedFromServer,
      conversationCount,
      conversationLimit,
      isAnonymous,
      anonymousChatUsed,
      teachingMode,
      teachingModeAvailable,
      setTeachingMode,
      ensureSession,
      sendMessage,
      resolveClarification,
      registerContext,
      unregisterContext,
      resetChat,
    }),
    [
      session,
      messages,
      isInitializing,
      isSending,
      pendingClarification,
      suggestions,
      capabilityLinks,
      hasReachedLimit,
      limitReachedFromServer,
      conversationCount,
      conversationLimit,
      isAnonymous,
      anonymousChatUsed,
      teachingMode,
      teachingModeAvailable,
      ensureSession,
      sendMessage,
      resolveClarification,
      registerContext,
      unregisterContext,
      resetChat,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = (): ChatContextValue => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
